import { useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { MarkdownViewer } from '../components/MarkdownViewer'
import { EditorPane } from '../components/EditorPane'
import { UuidBadge } from '../components/UuidBadge'
import { TabBar } from '../components/TabBar'
import { useDocument } from '../lib/queries'
import { useDocumentTabs } from '../lib/useDocumentTabs'
import { usePageTitle } from '../lib/usePageTitle'
import { triggerDownload } from '../lib/download'
import type { ProjectDto } from '../lib/types'

function formatBytes(n?: number): string {
  if (!n || n <= 0) return '0 B'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function DocumentView() {
  const { uuid, projectKey } = useParams<{ uuid: string; projectKey: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const editing = searchParams.get('edit') === '1'
  const ctx = useOutletContext<{ project: ProjectDto | undefined }>()
  const doc = useDocument(uuid)
  const tabs = useDocumentTabs()
  usePageTitle(doc.data?.title)
  // 진입한 문서를 글로벌 탭에 등록 (이미 있으면 lastActiveAt 갱신)
  useEffect(() => {
    if (doc.data) tabs.open({ uuid: doc.data.uuid, title: doc.data.title, projectKey: ctx.project?.projectKey ?? projectKey })
  }, [doc.data?.uuid, doc.data?.title, ctx.project?.projectKey])  // eslint-disable-line react-hooks/exhaustive-deps
  const [dark, setDark] = useState<boolean>(() => localStorage.getItem('doc-viewer-theme') === 'dark')
  const [fontSize, setFontSize] = useState<number>(() => parseInt(localStorage.getItem('doc-font-size') || '16', 10))
  const [fabOpen, setFabOpen] = useState(false)

  const applyDark = (v: boolean) => { setDark(v); localStorage.setItem('doc-viewer-theme', v ? 'dark' : 'light') }
  const applyFont = (n: number) => { setFontSize(n); localStorage.setItem('doc-font-size', String(n)) }

  const d = doc.data
  const docHref = (sfx: string) => `/d/${uuid}${sfx}`

  // editing 모드면 EditorPane (자체 toolbar) 만 렌더 — Viewer / FAB 는 숨김
  if (editing && d) {
    return (
      <main className={`split-main${dark ? ' viewer-dark' : ''}`}>
        <TabBar />
        <EditorPane doc={d} onLeaveEdit={() => setSearchParams({})} />
      </main>
    )
  }

  return (
    <main className={`split-main${dark ? ' viewer-dark' : ''}`}>
      <TabBar />
      <div className="doc-tab-content" style={{ '--doc-font-size': `${fontSize}px` } as React.CSSProperties}>
        <div className={`doc-fab-stack${fabOpen ? ' expanded' : ''}`} id="doc-fab-stack">
          <button
            type="button"
            className="doc-fab doc-fab-toggle"
            data-tooltip="Document actions"
            aria-label="Document actions"
            aria-expanded={fabOpen}
            onClick={() => setFabOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
          </button>
          <div className="doc-fab-menu">
            <button id="btn-versions" type="button" className="doc-fab" data-tooltip="Versions" aria-label="Versions"
                    onClick={() => navigate(docHref('/versions'))}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            </button>
            <button id="btn-edit" type="button" className="doc-fab" data-tooltip="Edit" aria-label="Edit"
                    onClick={() => setSearchParams({ edit: '1' })}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>
            </button>
            <button id="btn-download-md" type="button" className="doc-fab" data-tooltip="Download as MD" aria-label="Download as MD"
                    onClick={() => uuid && triggerDownload(`/api/documents/${uuid}/export?format=md`)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
            <button id="btn-download-pdf" type="button" className="doc-fab" data-tooltip="Download as PDF" aria-label="Download as PDF"
                    onClick={() => uuid && triggerDownload(`/api/documents/${uuid}/export?format=pdf`)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><text x="7.5" y="18" font-size="6" font-weight="bold" fill="currentColor" stroke="none">PDF</text></svg>
            </button>
            <button type="button" className="doc-fab" data-tooltip="Font size +" aria-label="Font size larger"
                    onClick={() => applyFont(Math.min(24, fontSize + 1))}>A+</button>
            <button type="button" className="doc-fab" data-tooltip="Reset font size" aria-label="Reset font size"
                    onClick={() => applyFont(16)}>A0</button>
            <button type="button" className="doc-fab" data-tooltip="Font size -" aria-label="Font size smaller"
                    onClick={() => applyFont(Math.max(12, fontSize - 1))}>A-</button>
            <button type="button" className="doc-fab" data-tooltip="Toggle theme" aria-label="Toggle viewer theme"
                    onClick={() => applyDark(!dark)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
            </button>
          </div>
        </div>
        {d ? (
          <>
            <h2 id="doc-title" style={{ fontSize: '1.5rem', marginBottom: 4 }}>{d.title}</h2>
            <div id="doc-metadata" style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span>v{d.version} · {new Date(d.updatedAt).toLocaleString()} · {formatBytes(d.sizeBytes)}</span>
              <UuidBadge uuid={d.uuid} />
            </div>
            <MarkdownViewer content={d.content} dark={dark} />
          </>
        ) : doc.isError ? (
          <p style={{ color: 'var(--text-muted)' }}>Document not found.</p>
        ) : null}
      </div>
    </main>
  )
}
